/**
 * Extraction de texte depuis une facture uploadée, côté navigateur.
 *
 * - PDF  → pdfjs-dist  (extraction textuelle native, rapide)
 * - Image → tesseract.js (OCR lang `fra`)
 *
 * Les deux librairies sont importées dynamiquement pour ne pas alourdir
 * le bundle principal. L'analyse échoue silencieusement (dégradation gracieuse)
 * : l'upload n'est jamais bloqué par l'OCR.
 */

import { analyzeBillText, type BillInsights } from '@shared/analysis/bill-insights';

/* ────────────────────────────────────────────────────────────────────────────
   Extraction PDF
──────────────────────────────────────────────────────────────────────────── */

async function extractPdfText(file: File): Promise<string> {
  const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');

  // Le worker doit être servi statiquement — en développement, on le pointe vers
  // le CDN jsdelivr pour éviter la config Vite ; en prod, copier le fichier worker.
  GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${(await import('pdfjs-dist')).version}/build/pdf.worker.min.mjs`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await getDocument({ data: arrayBuffer }).promise;

  const textParts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    textParts.push(pageText);
  }

  return textParts.join('\n');
}

/* ────────────────────────────────────────────────────────────────────────────
   OCR Image (Tesseract)
──────────────────────────────────────────────────────────────────────────── */

async function extractImageText(file: File): Promise<string> {
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('fra');
  try {
    const { data } = await worker.recognize(file);
    return data.text;
  } finally {
    await worker.terminate();
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Raffinement IA serveur (optionnel — activé si MISTRAL_API_KEY configurée)
──────────────────────────────────────────────────────────────────────────── */

interface AiBillExtraction {
  supplierName: string | null;
  energyType: 'electricity' | 'gas' | 'unknown';
  annualConsumptionKwh: number | null;
  subscribedPowerKva: number | null;
  totalAmountEur: number | null;
  supplyAmountHt: number | null;
  turpeAmountEur: number | null;
  cspeAmountEur: number | null;
  contractType: 'fixed' | 'indexed' | 'regulated' | 'unknown';
  contractExpiryDate: string | null;
  billPeriod: string | null;
  pointDeliveryId: string | null;
}

/**
 * Tente le raffinement IA côté serveur. Retourne null silencieusement si :
 * la feature n'est pas configurée (503), rate-limit (429), erreur réseau.
 */
async function refineWithAi(text: string): Promise<AiBillExtraction | null> {
  try {
    const res = await fetch('/api/ai/analyze-bill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.slice(0, 50_000) }),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { extraction?: AiBillExtraction };
    return json.extraction ?? null;
  } catch {
    return null;
  }
}

/**
 * Fusionne l'extraction IA dans les insights heuristiques.
 * Les champs IA non-null priment (extraction plus fiable que les regex),
 * puis les dérivés (prix unitaire, leviers, score) sont recalculés.
 */
function mergeAiExtraction(base: BillInsights, ai: AiBillExtraction): BillInsights {
  const merged: BillInsights = {
    ...base,
    supplierName: ai.supplierName ?? base.supplierName,
    energyType: ai.energyType !== 'unknown' ? ai.energyType : base.energyType,
    annualConsumptionKwh: ai.annualConsumptionKwh ?? base.annualConsumptionKwh,
    subscribedPowerKva: ai.subscribedPowerKva ?? base.subscribedPowerKva,
    totalAmountEur: ai.totalAmountEur ?? base.totalAmountEur,
    supplyAmountHt: ai.supplyAmountHt ?? base.supplyAmountHt,
    contractType: ai.contractType !== 'unknown' ? ai.contractType : base.contractType,
    contractExpiryDate: ai.contractExpiryDate ?? base.contractExpiryDate,
    billPeriod: ai.billPeriod ?? base.billPeriod,
    pointDeliveryId: ai.pointDeliveryId ?? base.pointDeliveryId,
    taxes: {
      ...base.taxes,
      turpe: base.taxes.turpe || ai.turpeAmountEur !== null,
      turpeAmountEur: ai.turpeAmountEur ?? base.taxes.turpeAmountEur,
      cspe: base.taxes.cspe || ai.cspeAmountEur !== null,
      cspeAmountEur: ai.cspeAmountEur ?? base.taxes.cspeAmountEur,
    },
  };

  // Recalcule le prix unitaire avec les valeurs raffinées
  if (merged.supplyAmountHt && merged.annualConsumptionKwh && merged.annualConsumptionKwh > 0) {
    const raw = merged.supplyAmountHt / merged.annualConsumptionKwh;
    if (raw >= 0.01 && raw <= 0.5) merged.unitPriceEurPerKwh = raw;
  }

  // Recalcule les leviers suggérés
  const levers: Array<'turpe' | 'cspe'> = [];
  if (merged.taxes.turpe) levers.push('turpe');
  if (merged.taxes.cspe) levers.push('cspe');
  merged.suggestedLevers = levers;

  // L'IA améliore la confiance si elle a trouvé les champs clés
  if (merged.supplierName && merged.annualConsumptionKwh && merged.totalAmountEur) {
    merged.confidence = 'high';
  }

  return merged;
}

/* ────────────────────────────────────────────────────────────────────────────
   Point d'entrée public
──────────────────────────────────────────────────────────────────────────── */

/**
 * Analyse une facture et retourne les insights.
 *
 * Pipeline : extraction texte (PDF/OCR) → analyse heuristique locale →
 * raffinement IA serveur si configuré (silencieux sinon).
 *
 * Retourne `null` si le fichier ne peut pas être analysé (format non supporté,
 * OCR échoue, etc.) — l'upload reste toujours possible.
 */
export async function extractBillInsights(file: File): Promise<BillInsights | null> {
  try {
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');

    let text = '';
    let source: BillInsights['source'] = 'unknown';

    if (isPdf) {
      text = await extractPdfText(file);
      source = 'pdf-text';
    } else if (isImage) {
      text = await extractImageText(file);
      source = 'ocr';
    } else {
      return null;
    }

    if (!text.trim()) return null;

    const heuristic = analyzeBillText({ text, source });

    // Raffinement IA (non bloquant pour la qualité minimale : si l'IA échoue
    // ou n'est pas configurée, on garde l'analyse heuristique).
    const ai = await refineWithAi(text);
    return ai ? mergeAiExtraction(heuristic, ai) : heuristic;
  } catch (err) {
    // Dégradation gracieuse : on log en dev mais on ne bloque pas l'utilisateur
    if (typeof window !== 'undefined' && (import.meta as unknown as { env: { DEV?: boolean } }).env?.DEV) {
      console.warn('[bill-extract] Analyse échouée :', err);
    }
    return null;
  }
}
